import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlay, faImage, faVideo, faFilter, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../../context/NotificationContext';
import { getApiUrl } from '../../../config/api';
import { MediaItem, LibraryItem } from '../../../types';
import styles from './LibraryTab.module.css';
import { useTranslation } from 'react-i18next';

interface LibraryTabProps {
  projectId: string;
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  mediaItems: MediaItem[];
  addAtCurrentTime?: boolean; // NEW: checkbox state
  currentTime?: number; // NEW: current timeline position
}

type FilterType = 'all' | 'images' | 'videos';

const LibraryTab: React.FC<LibraryTabProps> = ({ 
  projectId, 
  setMediaItems, 
  mediaItems,
  addAtCurrentTime = false, // NEW: default to false
  currentTime = 0, // NEW: default to 0
}) => {
  const { t } = useTranslation('videoEditor');
  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<LibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [counts, setCounts] = useState({ images: 0, videos: 0, audio: 0 });
  
  const { showNotification } = useNotification();

  // Load library items
  useEffect(() => {
    if (projectId) {
      loadLibraryItems();
    }
  }, [projectId]);

  // Apply filter when filter changes or library items change
  useEffect(() => {
    applyFilter();
  }, [filter, libraryItems]);

  const loadLibraryItems = async () => {
    try {
      setIsLoading(true);
      
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(getApiUrl(`/media/library/${projectId}`), {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch library: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Filter out audio items since we only want images and videos
        const visualItems = data.data.filter((item: LibraryItem) => 
          item.type === 'image' || item.type === 'video'
        );
        
        // Sort by newest first (createdAt descending)
        const sortedItems = visualItems.sort((a: LibraryItem, b: LibraryItem) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        
        setLibraryItems(sortedItems);
        setCounts(data.counts);
      } else {
        throw new Error(data.message || 'Failed to fetch library');
      }
    } catch (error) {
      console.error('Error loading library items:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to load library items',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilter = () => {
    let filtered = libraryItems;
    
    switch (filter) {
      case 'images':
        filtered = libraryItems.filter(item => item.type === 'image');
        break;
      case 'videos':
        filtered = libraryItems.filter(item => item.type === 'video');
        break;
      case 'all':
      default:
        filtered = libraryItems;
        break;
    }
    
    setFilteredItems(filtered);
  };

  const handleDeleteItem = async (item: LibraryItem) => {
    if (!confirm(t('videoEditor.library.deleteConfirm', { name: item.name }))) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(getApiUrl(`/media/library/${projectId}`), {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ url: item.url }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete item: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Remove from local state
        setLibraryItems(prev => prev.filter(libItem => libItem.id !== item.id));
        // showNotification({
        //   title: 'Success',
        //   message: 'Item deleted successfully',
        //   type: 'success'
        // });
      } else {
        throw new Error(data.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to delete item',
        type: 'error'
      });
    }
  };

  const handleAddToTimeline = (item: LibraryItem) => {
    console.log('📚 Library - add to timeline started:', { addAtCurrentTime, currentTime, itemName: item.name });
    
    setMediaItems(prevItems => {
      // Calculate insertion position based on checkbox state
      const duration = item.duration ||  5; // Default durations
      const targetStart = addAtCurrentTime ? currentTime : (prevItems.length > 0 ? prevItems[prevItems.length - 1].end : 0);
      let start = targetStart;
      let end = targetStart + duration;

      console.log('📚 Library - calculating position:', { addAtCurrentTime, currentTime, targetStart, duration });

      if (addAtCurrentTime) {
        // Check for conflicts and find available space
        const hasConflict = prevItems.some(existingItem => 
          (start >= existingItem.start && start < existingItem.end) ||
          (end > existingItem.start && end <= existingItem.end) ||
          (start <= existingItem.start && end >= existingItem.end)
        );

        console.log('📚 Library - conflict check:', { hasConflict, start, end });
      }

      console.log('📚 Library - final position:', { start, end });

      // Create a new media item to add to timeline
      const newMediaItem: MediaItem = {
        id: `library-${Date.now()}-${Math.random()}`,
        name: item.name,
        start,
        end,
        url: item.url,
        type: item.type === 'video' ? 'video' : 'image',
        length: duration,
        src: item.url,
      };

      // Insert and sort by start time
      const newItems = [...prevItems, newMediaItem].sort((a, b) => a.start - b.start);
      return newItems;
    });
    
    // showNotification({
    //   title: 'Added to Timeline',
    //   message: `"${item.name}" has been added to the timeline`,
    //   type: 'success'
    // });
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>{t('videoEditor.library.loading')}</p>
      </div>
    );
  }

  return (
    <div className={styles.libraryContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>{t('videoEditor.library.title')}</h3>
          <button 
            className={styles.refreshButton}
            onClick={loadLibraryItems}
            disabled={isLoading}
            title={t('videoEditor.library.refresh')}
          >
            <FontAwesomeIcon icon={faRefresh} className={isLoading ? styles.spinning : ''} />
          </button>
        </div>
        <p className={styles.subtitle}>
          {libraryItems.length} {t('videoEditor.library.items')} • {t('videoEditor.library.clickToAdd')}
        </p>
      </div>

      {/* Filter Controls */}
      <div className={styles.filterContainer}>
        <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
        <div className={styles.filterButtons}>
          <button
            className={`${styles.filterButton} ${filter === 'all' ? styles.active : ''}`}
            onClick={() => setFilter('all')}
          >
            {t('videoEditor.library.filterAll')} ({counts.images + counts.videos})
          </button>
          <button
            className={`${styles.filterButton} ${filter === 'videos' ? styles.active : ''}`}
            onClick={() => setFilter('videos')}
          >
            {t('videoEditor.library.filterVideos')} ({counts.videos})
          </button>
          <button
            className={`${styles.filterButton} ${filter === 'images' ? styles.active : ''}`}
            onClick={() => setFilter('images')}
          >
            {t('videoEditor.library.filterImages')} ({counts.images})
          </button>
        </div>
      </div>

      {/* Library Grid */}
      <div className={styles.libraryGrid}>
        {filteredItems.length === 0 ? (
          <div className={styles.emptyState}>
            <FontAwesomeIcon 
              icon={filter === 'videos' ? faVideo : faImage} 
              size="2x" 
              className={styles.emptyIcon} 
            />
            <p>
              {filter === 'all' 
                ? t('videoEditor.library.noItemsFound') 
                : filter === 'videos' 
                  ? t('videoEditor.library.noVideosFound')
                  : t('videoEditor.library.noImagesFound')
              }
            </p>
            <small>{t('videoEditor.library.emptyStateHint')}</small>
          </div>
        ) : (
          filteredItems.map((item) => (
            <div key={item.id} className={styles.libraryItem}>
              {/* Thumbnail */}
              <div className={styles.thumbnailContainer} onClick={() => handleAddToTimeline(item)}>
                {item.type === 'video' ? (
                  <video
                    src={item.url}
                    className={styles.thumbnail}
                    muted
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={item.url}
                    alt={item.name}
                    className={styles.thumbnail}
                  />
                )}
                
                {/* Type indicator */}
                <div className={styles.typeIndicator}>
                  <FontAwesomeIcon 
                    icon={item.type === 'video' ? faVideo : faImage} 
                    className={styles.typeIcon}
                  />
                </div>

                {/* Duration for videos */}
                {item.type === 'video' && item.duration && (
                  <div className={styles.duration}>
                    {formatDuration(item.duration)}
                  </div>
                )}

                {/* Source indicator */}
                <div className={`${styles.sourceIndicator} ${styles[item.source]}`}>
                  {item.source === 'ai-generated' ? 'AI' : 'UP'}
                </div>

                {/* Hover overlay */}
                <div className={styles.hoverOverlay}>
                  <FontAwesomeIcon icon={faPlay} className={styles.playIcon} />
                  <span>{t('videoEditor.library.addToTimeline')}</span>
                </div>
              </div>

              {/* Item info */}
              <div className={styles.itemInfo}>
                <h4 className={styles.itemName} title={item.name}>
                  {item.name}
                </h4>
                
                {item.prompt && (
                  <p className={styles.itemPrompt} title={item.prompt}>
                    {item.prompt}
                  </p>
                )}

                <div className={styles.itemMeta}>
                  <span className={styles.itemDate}>
                    {new Date(item.createdAt).toLocaleDateString()}
                  </span>
                  {item.seed && (
                    <span className={styles.itemSeed}>
                      Seed: {item.seed}
                    </span>
                  )}
                </div>
              </div>

              {/* Delete button */}
              <button
                className={styles.deleteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteItem(item);
                }}
                title={t('videoEditor.library.deleteButton')}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LibraryTab;
